"use client";

import React from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Search, X } from "lucide-react";
import { Input } from "../input.js";
import { Button } from "../button.js";
import { Badge } from "../badge.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select.js";
import { useAdvancedList, type UseAdvancedListOptions } from "./useAdvancedList.js";
import { AdvancedListSortableRow } from "./AdvancedListSortableRow.js";

export interface AdvancedListProps<T> extends UseAdvancedListOptions<T> {
  /** Render one item's content. Wrapped in a drag handle automatically when `onReorder` is set. */
  renderItem: (item: T) => React.ReactNode;
  /** Placeholder for the search box. Only shown when `searchFields` is set. */
  searchPlaceholder?: string;
  /** Shown when the query produces zero results. */
  emptyState?: React.ReactNode;
  /**
   * Enables drag-to-reorder (dnd-kit, same mechanism as the column manager's
   * "Shown columns" list). Receives the full reordered id list on drop.
   * Reordering is only meaningful with no active sort/search/filter — the
   * control bar still renders in that case, but a caller who wants a purely
   * manual order should typically not also offer a sort control.
   */
  onReorder?: (reorderedIds: string[]) => void;
  /** Cap the scrollable area height — the common case for a list inside a modal. */
  maxHeight?: string;
  className?: string;
}

/**
 * Shared search + filter + sort (+ optional drag-reorder, optional grouping)
 * list, sized for "more than ~20 items inside a modal." Built from the same
 * patterns as apps/hub's SimpleColumnManager (dnd-kit reorder, search-to-
 * filter) and the routines list's filter bar (pure query logic, chip-style
 * filters) — see useAdvancedList.ts / advanced-list-query.ts for the parts
 * usable standalone without this component's layout.
 */
export function AdvancedList<T>({
  renderItem,
  searchPlaceholder = "Search…",
  emptyState,
  onReorder,
  maxHeight = "24rem",
  className,
  ...listOptions
}: AdvancedListProps<T>) {
  const list = useAdvancedList(listOptions);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (!onReorder) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = list.visibleItems.map((item) => listOptions.getId(item));
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;
      onReorder(arrayMove(ids, oldIndex, newIndex));
    },
    [onReorder, list.visibleItems, listOptions]
  );

  const activeSortDef = listOptions.sorts?.find((s) => s.id === list.queryState.sortId);

  const renderRow = (item: T) => {
    const content = renderItem(item);
    return onReorder ? (
      <AdvancedListSortableRow key={listOptions.getId(item)} id={listOptions.getId(item)}>
        {content}
      </AdvancedListSortableRow>
    ) : (
      <div key={listOptions.getId(item)}>{content}</div>
    );
  };

  const body = (
    <div className="flex-1 space-y-2 overflow-y-auto" style={{ maxHeight }}>
      {list.visibleItems.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          {emptyState ?? "No items match."}
        </div>
      ) : (
        list.groupedItems.map((group) => (
          <div key={group.key || "__ungrouped"} className="space-y-2">
            {group.key ? (
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {group.key}
              </div>
            ) : null}
            {group.items.map(renderRow)}
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className={`flex min-h-0 flex-col gap-3 ${className ?? ""}`}>
      {listOptions.searchFields ? (
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            value={list.queryState.search}
            onChange={(e) => list.setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="pl-8"
          />
        </div>
      ) : null}

      {listOptions.filters && listOptions.filters.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {listOptions.filters.map((filterDef) => {
            const selected = list.queryState.activeFilters[filterDef.id] ?? [];
            return (
              <div key={filterDef.id} className="flex flex-wrap items-center gap-1">
                <span className="text-xs text-muted-foreground">{filterDef.label}:</span>
                {filterDef.options.map((option) => {
                  const isActive = selected.includes(option.value);
                  return (
                    <Badge
                      key={option.value}
                      role="button"
                      tabIndex={0}
                      variant={isActive ? "default" : "outline"}
                      className="cursor-pointer select-none"
                      onClick={() => list.toggleFilterValue(filterDef.id, option.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          list.toggleFilterValue(filterDef.id, option.value);
                        }
                      }}
                    >
                      {option.label}
                    </Badge>
                  );
                })}
              </div>
            );
          })}
          {list.hasActiveQuery ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={list.clearAllFilters}
              className="h-6 gap-1 px-2 text-xs"
            >
              <X className="h-3 w-3" aria-hidden />
              Clear
            </Button>
          ) : null}
        </div>
      ) : null}

      {listOptions.sorts && listOptions.sorts.length > 0 ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sort:</span>
          <Select
            value={list.queryState.sortId ?? "__none"}
            onValueChange={(value) => list.setSort(value === "__none" ? null : value)}
          >
            <SelectTrigger className="h-8 w-48 text-xs">
              <SelectValue placeholder="Default order" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Default order</SelectItem>
              {listOptions.sorts.map((sortDef) => (
                <SelectItem key={sortDef.id} value={sortDef.id}>
                  {sortDef.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeSortDef ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={list.toggleSortDirection}
              className="h-8 px-2 text-xs"
              aria-label={list.queryState.sortDescending ? "Sort ascending" : "Sort descending"}
            >
              {list.queryState.sortDescending ? "↓ Desc" : "↑ Asc"}
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {list.visibleCount} of {list.totalCount} shown
        </span>
      </div>

      {onReorder ? (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={list.visibleItems.map((item) => listOptions.getId(item))}
            strategy={verticalListSortingStrategy}
          >
            {body}
          </SortableContext>
        </DndContext>
      ) : (
        body
      )}
    </div>
  );
}
